/**
 * Application origin / referral tracking.
 * Values mirror Flutter AppConstants — `applicationPage` is sent to the API
 * as the application `page`, so the strings must match exactly.
 */

export const APPLICATION_PAGE = {
  CAREER_SEARCH: 'career-search',
  CAREER_JOB_SHARE: 'career-job-share',
  CAREER_RECOMMENDATION: 'career-recommendation',
  CAREER_RECOMMENDATION_MAIL: 'career-recommendation-mail',
} as const;

export type ApplicationPage = (typeof APPLICATION_PAGE)[keyof typeof APPLICATION_PAGE];

/** `ref` query param values on /jobs/:id links. */
export const JOB_SHARE_REF = {
  INTERNAL: 'job-share-internal-link',
  DIRECT: 'job-share-direct-link',
  FB: 'job-share-fb-link',
  X: 'job-share-x-link',
  LINKEDIN: 'job-share-linkedin-link',
} as const;

/*
 * Storage mirrors Flutter: the application origin lives in SharedPreferences and the referral
 * data in Hive, so both persist across tabs and sessions (localStorage). The referral data is
 * removed on sign-out (LocalDataService.deleteJobRefferalData); the origin is not.
 */
const JOB_REFERRAL_DATA_KEY = 'jobReferralData';

/** Mirrors Flutter GenericHelpers.setApplicationPageAndType(). */
export function setApplicationPageAndType(page: ApplicationPage, isRecommended: boolean): void {
  localStorage.setItem('applicationPage', page);
  localStorage.setItem('isRecommended', String(isRecommended));
}

/** Mirrors Flutter GenericHelpers.resetApplicationPageAndType() — no page is sent with the application. */
export function resetApplicationPageAndType(): void {
  localStorage.setItem('applicationPage', '');
  localStorage.setItem('isRecommended', 'false');
}

/**
 * Mirrors Flutter routing_config setDefaultApplicationOrigin(), run when /application and
 * /easyApplyApplication open: with no stored origin (e.g. a link from an email), the
 * application counts as career-recommendation-mail.
 */
export function setDefaultApplicationOrigin(): void {
  if (!localStorage.getItem('applicationPage')) {
    setApplicationPageAndType(APPLICATION_PAGE.CAREER_RECOMMENDATION_MAIL, false);
  }
}

/** Flutter GenericHelpers.getApplicationPage / getIsRecommended → the application's `page` + `isRecommended`. */
export function getApplicationPageFields(): { page?: string; isRecommended?: boolean } {
  const page = localStorage.getItem('applicationPage');
  return page ? { page, isRecommended: localStorage.getItem('isRecommended') === 'true' } : {};
}

/** Flutter LocalDataService.getJobRefferalData(): channel / sourceName from a shared job link. */
export function getJobReferralData(): { channel?: string; sourceName?: string } {
  try {
    const data = localStorage.getItem(JOB_REFERRAL_DATA_KEY);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
}

/** Flutter LocalDataService.deleteJobRefferalData(), called on sign-out. */
export function clearJobReferralData(): void {
  localStorage.removeItem(JOB_REFERRAL_DATA_KEY);
}

/**
 * Called when /jobs/:id is opened. Mirrors Flutter routing_config jobsRoute:
 * stores channel/sourceName for the application headers, then detectPageOrigin(ref).
 */
export function captureJobLinkOrigin(searchParams: URLSearchParams): void {
  const channel = searchParams.get('channel') ?? '';
  const sourceName = searchParams.get('sourceName') ?? '';
  if (channel || sourceName) {
    localStorage.setItem(JOB_REFERRAL_DATA_KEY, JSON.stringify({ channel, sourceName }));
  }

  const ref = searchParams.get('ref') ?? '';
  const isInternalNavigation = document.referrer.includes(window.location.host);
  if ((ref === '' || ref === JOB_SHARE_REF.INTERNAL) && isInternalNavigation) return;
  setApplicationPageAndType(APPLICATION_PAGE.CAREER_JOB_SHARE, false);
}
